import { z } from "zod";

/**
 * Custom proposals (remodels, repairs, commercial, anything that isn't a priced roof or an
 * insurance estimate). Built from sections of work, allowances, options and alternates the
 * customer picks when signing, unit prices, exclusions, qualifications, and a payment schedule:
 * milestones, or progress billing against a schedule of values with retainage (commercial).
 *
 * Units: money in whole cents, quantities in thousandths (32.5 = 32500), percents in thousandths
 * of a percent (30% = 30000, 8.625% = 8625). Everything rounds half up to the cent, here and only
 * here: the AI and the pages never do the arithmetic that ends up on a document.
 */

export const CUSTOM_MARKETS = ["residential", "commercial"] as const;
export type CustomMarket = (typeof CUSTOM_MARKETS)[number];
export const CUSTOM_MARKET_LABELS: Record<CustomMarket, string> = { residential: "Residential", commercial: "Commercial" };

/** How a section is priced: one price, the sum of its items, or part of the base bid with no price of its own. */
export const SECTION_PRICING = ["lump", "items", "included"] as const;
export type SectionPricing = (typeof SECTION_PRICING)[number];

/**
 * `aiSource` on anything priced: the AI suggested this number and the rep hasn't accepted it yet
 * ("NMT's 3 past bathroom remodels", "ABC price × quantity + labor rate"). It can't be sent until
 * it's accepted (the source is cleared) or changed.
 */
const aiSource = z.string().trim().max(300).nullable().default(null);
const cents = z.number().int().min(0).max(1_000_000_000);
const costCents = cents.nullable().default(null);
const qty = z.number().int().min(0).max(1_000_000_000);
const percentMilli = z.number().int().min(0).max(100_000);
const key = z.string().trim().min(1).max(40);
const lines = (n: number, len = 1000) => z.array(z.string().max(len)).max(n).default([]);

export const customItemSchema = z.object({
  id: key,
  description: z.string().trim().max(500),
  quantity: qty,
  unit: z.string().trim().max(20).default("EA"),
  unitPriceCents: cents,
  /** Our cost per unit (for the margin; never shown to the customer). */
  unitCostCents: costCents,
  aiSource,
});
export type CustomItem = z.infer<typeof customItemSchema>;

export const customSectionSchema = z.object({
  id: key,
  heading: z.string().trim().max(200),
  /** What we'll do, written for the customer. */
  description: z.string().max(10_000).default(""),
  /** "What's included" bullets. Blank lines are kept while the rep types; they're left off the proposal. */
  included: lines(100),
  pricing: z.enum(SECTION_PRICING).default("lump"),
  /** Lump-sum sections. */
  priceCents: cents.default(0),
  /** Our cost for a lump-sum or included section (items carry their own). */
  costCents,
  items: z.array(customItemSchema).max(200).default([]),
  aiSource,
});
export type CustomSection = z.infer<typeof customSectionSchema>;

/** "Tile: $12.00/sq ft, 180 sq ft" (per unit) or "Light fixtures: $1,500" (lump). Part of the price; overages by change order. */
export const customAllowanceSchema = z.object({
  id: key,
  description: z.string().trim().max(300),
  /** Per unit when both are set; otherwise `amountCents`. */
  quantity: qty.nullable().default(null),
  unit: z.string().trim().max(20).default(""),
  unitPriceCents: cents.nullable().default(null),
  amountCents: cents.default(0),
  costCents,
  aiSource,
});
export type CustomAllowance = z.infer<typeof customAllowanceSchema>;

/** An add-on ("Under-cabinet lighting +$1,450") or a deduct alternate ("Owner supplies fixtures −$3,200"), picked when signing. */
export const customOptionSchema = z.object({
  id: key,
  kind: z.enum(["add", "deduct"]).default("add"),
  /** "Option 1", "Alternate 2" (blank: numbered on the proposal). */
  label: z.string().trim().max(60).default(""),
  description: z.string().trim().max(1000),
  /** Always positive; a deduct comes off. */
  amountCents: cents,
  /** What it changes our cost by (positive), for the margin. */
  costCents,
  aiSource,
});
export type CustomOption = z.infer<typeof customOptionSchema>;

/** A price for extra work if it's needed ("Additional decking: $95/sheet"). Not in the total. */
export const customUnitPriceSchema = z.object({
  id: key,
  description: z.string().trim().max(300),
  unit: z.string().trim().max(20).default("EA"),
  unitPriceCents: cents,
  aiSource,
});
export type CustomUnitPrice = z.infer<typeof customUnitPriceSchema>;

/** A payment. The last one is always "the rest", so the payments always add up to the contract. */
export const customMilestoneSchema = z.object({
  id: key,
  label: z.string().trim().max(120),
  /** "When this agreement is signed", "When the cabinets are set". */
  when: z.string().trim().max(300).default(""),
  /** A percent of the contract, or a set amount (`percentMilli` wins when both are set). */
  percentMilli: percentMilli.nullable().default(null),
  amountCents: cents.nullable().default(null),
});
export type CustomMilestone = z.infer<typeof customMilestoneSchema>;

export const customBillingSchema = z.object({
  mode: z.enum(["milestones", "progress"]).default("milestones"),
  milestones: z.array(customMilestoneSchema).max(20).default([]),
  /** Progress billing: held back from each bill until final completion. */
  retainagePercentMilli: percentMilli.max(50_000).default(0),
  /** "Drops to 5% once the work is 50% complete" (whole percent complete). */
  stepDown: z.object({ atCompletePercent: z.number().int().min(1).max(99), toPercentMilli: percentMilli.max(50_000) }).nullable().default(null),
  /** Bills can include materials stored on site. */
  storedMaterials: z.boolean().default(false),
  /** "Net 30". */
  terms: z.string().trim().max(200).default("Net 30"),
});
export type CustomBilling = z.infer<typeof customBillingSchema>;

/** What the customer sees, beyond the total, options, and payment schedule (always shown). */
export const customShowSchema = z.object({
  sectionPrices: z.boolean().default(false),
  /** Quantities and unit prices of itemized sections. */
  itemDetail: z.boolean().default(false),
  allowanceAmounts: z.boolean().default(true),
  /** Progress billing: the schedule of values, line by line. */
  scheduleOfValues: z.boolean().default(true),
});
export type CustomShow = z.infer<typeof customShowSchema>;

/** The commercial cover block. */
export const customCoverSchema = z.object({
  projectName: z.string().trim().max(200).default(""),
  /** The job site when it isn't the customer's address. */
  location: z.string().trim().max(300).default(""),
  /** "November 3, 2026". */
  bidDate: z.string().trim().max(60).default(""),
  toCompany: z.string().trim().max(200).default(""),
  toContact: z.string().trim().max(200).default(""),
  /** Architect, engineer, or the plans this bid is based on. */
  basedOn: z.string().trim().max(500).default(""),
});
export type CustomCover = z.infer<typeof customCoverSchema>;

export const customFormSchema = z.object({
  v: z.literal(1).default(1),
  market: z.enum(CUSTOM_MARKETS),
  /** Who set the market: from the customer record, the rep, or the AI. The AI never changes the rep's pick. */
  marketSetBy: z.enum(["auto", "rep", "ai"]).default("auto"),
  title: z.string().trim().max(120),
  /** The opening paragraph of the scope. */
  intro: z.string().max(10_000).default(""),
  cover: customCoverSchema.default(() => customCoverSchema.parse({})),
  sections: z.array(customSectionSchema).max(60).default([]),
  allowances: z.array(customAllowanceSchema).max(60).default([]),
  options: z.array(customOptionSchema).max(40).default([]),
  unitPrices: z.array(customUnitPriceSchema).max(60).default([]),
  exclusions: lines(100),
  qualifications: lines(100),
  schedule: z.object({ start: z.string().trim().max(200).default(""), duration: z.string().trim().max(200).default(""), notes: z.string().max(2000).default("") }).default({ start: "", duration: "", notes: "" }),
  billing: customBillingSchema.default(() => customBillingSchema.parse({})),
  /** Sales tax on the contract, when the job is taxable (0: none, the usual for contract work). */
  taxPercentMilli: percentMilli.max(20_000).default(0),
  discountCents: cents.default(0),
  warranty: z.string().max(4000).default(""),
  notes: z.string().max(4000).default(""),
  /** Lines the customer fills in when signing ("Tile color"). */
  selections: z.array(z.string().trim().max(80)).max(10).default([]),
  show: customShowSchema.default(() => customShowSchema.parse({})),
  validDays: z.number().int().min(1).max(120).default(15),
  signerName: z.string().trim().max(160).default(""),
  signerEmail: z.string().trim().max(254).default(""),
});
export type CustomForm = z.infer<typeof customFormSchema>;

export const DEFAULT_CUSTOM_WARRANTY =
  "Workmanship warranty from substantial completion: one (1) year on all work unless a longer term is stated in this proposal. Materials and equipment carry their manufacturers' warranties. See the General Conditions.";

/** A short id for a new part of the form. */
export function newPartId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** A starting form: residential bills by milestones, commercial by progress with 5% retainage. */
export function newCustomForm(market: CustomMarket, signerName: string, signerEmail: string, title = ""): CustomForm {
  return customFormSchema.parse({
    market,
    title: title || (market === "commercial" ? "Commercial proposal" : "Proposal"),
    sections: [{ id: newPartId(), heading: "", pricing: "lump" }],
    billing:
      market === "commercial"
        ? { mode: "progress", retainagePercentMilli: 5_000, terms: "Net 30", milestones: [] }
        : { mode: "milestones", milestones: [{ id: newPartId(), label: "Due on completion", when: "When the work is complete" }] },
    show: { sectionPrices: market === "commercial", itemDetail: false, allowanceAmounts: true, scheduleOfValues: true },
    warranty: DEFAULT_CUSTOM_WARRANTY,
    signerName,
    signerEmail,
  });
}

// ── Money ────────────────────────────────────────────────────────────────

/** a × b ÷ d, rounded half away from zero, in whole numbers (no float error). */
function mulDiv(a: number, b: number, d: number): number {
  const sign = Math.sign(a) * Math.sign(b) || 1;
  const n = Math.abs(a) * Math.abs(b);
  const q = Math.floor(n / d) + ((n % d) * 2 >= d ? 1 : 0);
  return q === 0 ? 0 : sign * q;
}

/** Quantity (thousandths) × a unit price, to the cent. */
export function extend(quantity: number, unitCents: number): number {
  return mulDiv(quantity, unitCents, 1000);
}

/** A percent (thousandths of a percent) of an amount, to the cent. */
export function percentOf(amountCents: number, pctMilli: number): number {
  return mulDiv(amountCents, pctMilli, 100_000);
}

/** "30%", "8.625%". */
export function formatPercentMilli(p: number): string {
  return `${(p / 1000).toLocaleString("en-US", { maximumFractionDigits: 3 })}%`;
}

/** "8.625" or "8.625%" → 8625; null when it isn't a percent with at most three decimals. */
export function parsePercentMilli(text: string): number | null {
  const t = text.trim().replace(/%$/, "").trim();
  if (!/^\d{1,3}(\.\d{0,3})?$/.test(t)) return null;
  const [whole, frac = ""] = t.split(".");
  const n = Number(whole) * 1000 + Number(frac.padEnd(3, "0"));
  return n <= 100_000 ? n : null;
}

export interface SectionTotal {
  id: string;
  /** 0 for an included section. */
  priceCents: number;
  costCents: number;
  /** false when some of its cost wasn't entered. */
  costKnown: boolean;
}

export function sectionTotal(s: CustomSection): SectionTotal {
  if (s.pricing === "items") {
    let price = 0;
    let cost = 0;
    let known = s.items.length > 0;
    for (const i of s.items) {
      price += extend(i.quantity, i.unitPriceCents);
      if (i.unitCostCents === null) known = false;
      else cost += extend(i.quantity, i.unitCostCents);
    }
    return { id: s.id, priceCents: price, costCents: cost, costKnown: known };
  }
  return { id: s.id, priceCents: s.pricing === "lump" ? s.priceCents : 0, costCents: s.costCents ?? 0, costKnown: s.costCents !== null };
}

export function allowanceAmount(a: Pick<CustomAllowance, "quantity" | "unitPriceCents" | "amountCents">): number {
  return a.quantity !== null && a.unitPriceCents !== null ? extend(a.quantity, a.unitPriceCents) : a.amountCents;
}

/** What the contract is figured from: the same for the rep's form and the proposal as sent. */
export interface PricingCore {
  sectionsCents: number;
  allowancesCents: number;
  discountCents: number;
  options: { id: string; kind: "add" | "deduct"; amountCents: number }[];
  taxPercentMilli: number;
}

export interface ContractTotals {
  sectionsCents: number;
  allowancesCents: number;
  /** The discount as applied (never more than the work). */
  discountCents: number;
  /** Sections + allowances − discount: the base bid before tax. */
  baseCents: number;
  /** The picked options, added and deducted. */
  optionsCents: number;
  /** Base + options (never below 0), before tax. */
  subtotalCents: number;
  taxCents: number;
  /** What the customer pays. */
  contractCents: number;
}

/** The contract with the options picked (none: the base bid). */
export function contractTotals(core: PricingCore, picked: readonly string[] = []): ContractTotals {
  const work = core.sectionsCents + core.allowancesCents;
  const discountCents = Math.min(Math.max(0, core.discountCents), work);
  const baseCents = work - discountCents;
  const ids = new Set(picked);
  let optionsCents = 0;
  for (const o of core.options) if (ids.has(o.id)) optionsCents += o.kind === "deduct" ? -o.amountCents : o.amountCents;
  const subtotalCents = Math.max(0, baseCents + optionsCents);
  const taxCents = percentOf(subtotalCents, core.taxPercentMilli);
  return { sectionsCents: core.sectionsCents, allowancesCents: core.allowancesCents, discountCents, baseCents, optionsCents, subtotalCents, taxCents, contractCents: subtotalCents + taxCents };
}

export function pricingCore(f: CustomForm): PricingCore {
  return {
    sectionsCents: f.sections.reduce((t, s) => t + sectionTotal(s).priceCents, 0),
    allowancesCents: f.allowances.reduce((t, a) => t + allowanceAmount(a), 0),
    discountCents: f.discountCents,
    options: f.options.map((o) => ({ id: o.id, kind: o.kind, amountCents: o.amountCents })),
    taxPercentMilli: f.taxPercentMilli,
  };
}

export interface MilestoneAmount {
  id: string;
  label: string;
  when: string;
  amountCents: number;
  /** As entered (null for a set amount, or the last payment: the rest). */
  percentMilli: number | null;
  /** The last payment: whatever's left. */
  rest: boolean;
}

/**
 * The payments for a contract total. Each but the last is its percent of the contract (to the
 * cent) or its set amount; the last is the rest, so they always add up. No payments: one, due on
 * completion.
 */
export function splitMilestones(milestones: readonly CustomMilestone[], contractCents: number): { payments: MilestoneAmount[]; problems: string[] } {
  const list = milestones.length ? milestones : [{ id: "completion", label: "Due on completion", when: "When the work is complete", percentMilli: null, amountCents: null }];
  const payments: MilestoneAmount[] = [];
  let used = 0;
  list.forEach((m, i) => {
    const rest = i === list.length - 1;
    const amount = rest ? contractCents - used : m.percentMilli !== null ? percentOf(contractCents, m.percentMilli) : (m.amountCents ?? 0);
    used += rest ? 0 : amount;
    payments.push({ id: m.id, label: m.label, when: m.when, amountCents: amount, percentMilli: rest ? null : m.percentMilli, rest });
  });
  const problems: string[] = [];
  const last = payments[payments.length - 1]!;
  if (last.amountCents < 0) problems.push("The payments add up to more than the contract: lower one so the last payment isn't negative.");
  else if (payments.length > 1 && last.amountCents === 0) problems.push(`Nothing is left for "${last.label || "the last payment"}": remove it or lower an earlier one.`);
  if (payments.some((p) => !p.rest && p.amountCents === 0)) problems.push("Every payment needs a percent or an amount.");
  if (payments.some((p) => !p.label.trim())) problems.push("Every payment needs a name.");
  return { payments, problems };
}

/** One line of the schedule of values (progress billing): what each bill's percent complete is measured against. */
export interface SovLine {
  key: string;
  description: string;
  valueCents: number;
}

/** What the schedule of values is made from: the form's or the sent proposal's priced parts. */
export interface SovSource {
  sections: { id: string; heading: string; pricing: SectionPricing; priceCents: number }[];
  allowances: { id: string; description: string; amountCents: number }[];
  options: { id: string; kind: "add" | "deduct"; label: string; description: string; amountCents: number }[];
  discountCents: number;
  taxPercentMilli: number;
}

/**
 * The schedule of values: each priced section, each allowance, each picked option, then the
 * discount and tax, so the lines add up to the contract. Included sections have no line (their
 * work is in the priced ones).
 */
export function scheduleOfValues(src: SovSource, picked: readonly string[] = []): SovLine[] {
  const out: SovLine[] = [];
  src.sections.forEach((s, i) => {
    if (s.pricing !== "included") out.push({ key: `s:${s.id}`, description: s.heading || `Section ${i + 1}`, valueCents: s.priceCents });
  });
  for (const a of src.allowances) out.push({ key: `a:${a.id}`, description: `Allowance: ${a.description}`, valueCents: a.amountCents });
  const ids = new Set(picked);
  src.options.forEach((o, i) => {
    if (ids.has(o.id)) out.push({ key: `o:${o.id}`, description: `${optionLabel(o, i, src.options)}: ${o.description}`, valueCents: o.kind === "deduct" ? -o.amountCents : o.amountCents });
  });
  const t = contractTotals(
    {
      sectionsCents: src.sections.reduce((n, s) => n + (s.pricing === "included" ? 0 : s.priceCents), 0),
      allowancesCents: src.allowances.reduce((n, a) => n + a.amountCents, 0),
      discountCents: src.discountCents,
      options: src.options,
      taxPercentMilli: src.taxPercentMilli,
    },
    picked,
  );
  if (t.discountCents) out.push({ key: "discount", description: "Discount", valueCents: -t.discountCents });
  // Deducts bigger than the work: the contract floors at $0, so the lines do too.
  const pre = out.reduce((n, l) => n + l.valueCents, 0);
  if (pre < 0) out.push({ key: "floor", description: "Adjustment", valueCents: -pre });
  if (t.taxCents) out.push({ key: "tax", description: `Sales tax (${formatPercentMilli(src.taxPercentMilli)})`, valueCents: t.taxCents });
  return out;
}

/** The form's schedule of values (the editor's preview). */
export function formSov(f: CustomForm, picked: readonly string[] = []): SovLine[] {
  return scheduleOfValues(
    {
      sections: f.sections.map((s) => ({ id: s.id, heading: s.heading, pricing: s.pricing, priceCents: sectionTotal(s).priceCents })),
      allowances: f.allowances.map((a) => ({ id: a.id, description: a.description, amountCents: allowanceAmount(a) })),
      options: f.options,
      discountCents: f.discountCents,
      taxPercentMilli: f.taxPercentMilli,
    },
    picked,
  );
}

/** "Option 1", "Alternate 2", or the rep's own label. Adds and deducts are numbered separately. */
export function optionLabel(o: Pick<CustomOption, "kind" | "label">, index: number, all?: readonly Pick<CustomOption, "kind">[]): string {
  if (o.label.trim()) return o.label.trim();
  const n = all ? all.slice(0, index + 1).filter((x) => x.kind === o.kind).length : index + 1;
  return `${o.kind === "deduct" ? "Alternate" : "Option"} ${n}`;
}

export interface CustomTotals extends ContractTotals {
  sections: SectionTotal[];
  allowances: { id: string; amountCents: number }[];
  /** Our cost of the base bid (sections and allowances; allowances without a cost count at their amount). */
  costCents: number;
  marginCents: number;
  /** Whole percent of the base before tax; null when there's no price or no cost entered at all. */
  marginPercent: number | null;
  /** Priced parts with no cost entered (the margin leaves them out). */
  missingCosts: number;
  belowMinimum: boolean;
  payments: MilestoneAmount[];
}

/** Everything the editor shows, for the base bid (no options picked). */
export function customTotals(f: CustomForm, minMarginPercent: number): CustomTotals & { problems: string[] } {
  const sections = f.sections.map(sectionTotal);
  const allowances = f.allowances.map((a) => ({ id: a.id, amountCents: allowanceAmount(a) }));
  const t = contractTotals(pricingCore(f));
  let costCents = 0;
  let entered = 0;
  let missingCosts = 0;
  for (const s of sections) {
    if (s.costKnown) entered++;
    else if (s.priceCents > 0) missingCosts++;
    costCents += s.costCents;
  }
  f.allowances.forEach((a, i) => {
    // An allowance is usually passed through at cost.
    costCents += a.costCents ?? allowances[i]!.amountCents;
    if (a.costCents !== null) entered++;
  });
  const marginCents = t.baseCents - costCents;
  const marginPercent = t.baseCents > 0 && entered > 0 ? Math.round((marginCents / t.baseCents) * 100) : null;
  const split = f.billing.mode === "milestones" ? splitMilestones(f.billing.milestones, t.contractCents) : { payments: [], problems: [] };
  return {
    ...t,
    sections,
    allowances,
    costCents,
    marginCents,
    marginPercent,
    missingCosts,
    belowMinimum: marginPercent !== null && marginPercent < minMarginPercent,
    payments: split.payments,
    problems: [...customProblems(f, t), ...split.problems],
  };
}

/** Number of prices the AI suggested that the rep hasn't accepted yet. */
export function unacceptedEstimates(f: CustomForm): number {
  let n = 0;
  for (const s of f.sections) {
    if (s.aiSource && s.pricing !== "included") n++;
    if (s.pricing === "items") n += s.items.filter((i) => i.aiSource).length;
  }
  for (const list of [f.allowances, f.options, f.unitPrices]) n += list.filter((x) => x.aiSource).length;
  return n;
}

/** Why it can't be sent yet (empty when it can). The margin is a warning, not a blocker. */
export function customProblems(f: CustomForm, t: ContractTotals = contractTotals(pricingCore(f))): string[] {
  const out: string[] = [];
  if (!f.title.trim()) out.push("Give the proposal a title.");
  if (!f.sections.length) out.push("Add at least one section of work.");
  if (f.sections.some((s) => !s.heading.trim())) out.push("Every section needs a heading.");
  for (const s of f.sections) {
    if (s.pricing === "items" && !s.items.length) out.push(`${s.heading || "A section"}: add its items, or price it another way.`);
    if (s.pricing === "items" && s.items.some((i) => !i.description.trim())) out.push(`${s.heading || "A section"}: every item needs a description.`);
  }
  if (f.allowances.some((a) => !a.description.trim())) out.push("Every allowance needs a description.");
  if (f.options.some((o) => !o.description.trim())) out.push("Every option needs a description.");
  if (f.unitPrices.some((u) => !u.description.trim())) out.push("Every unit price needs a description.");
  if (t.contractCents <= 0) out.push("The total has to be more than $0.");
  const ai = unacceptedEstimates(f);
  if (ai) out.push(`Accept or change the ${ai === 1 ? "AI estimate" : `${ai} AI estimates`} first.`);
  return out;
}

// ── As sent ──────────────────────────────────────────────────────────────

/** A custom proposal as sent (frozen): what the PDF and the customer's page show. Costs are left out. */
export interface CustomSnapshot {
  market: CustomMarket;
  title: string;
  /** 0 for the first one sent; Rev 1, Rev 2… after. */
  revision: number;
  cover: CustomCover;
  intro: string;
  sections: {
    id: string;
    heading: string;
    description: string;
    included: string[];
    pricing: SectionPricing;
    priceCents: number;
    items: { description: string; quantity: number; unit: string; unitPriceCents: number; amountCents: number }[];
  }[];
  allowances: { id: string; description: string; quantity: number | null; unit: string; unitPriceCents: number | null; amountCents: number }[];
  options: { id: string; kind: "add" | "deduct"; label: string; description: string; amountCents: number }[];
  unitPrices: { description: string; unit: string; unitPriceCents: number }[];
  exclusions: string[];
  qualifications: string[];
  schedule: { start: string; duration: string; notes: string };
  billing: Omit<CustomBilling, "milestones"> & { milestones: CustomMilestone[] };
  taxPercentMilli: number;
  discountCents: number;
  /** The base bid (no options). */
  totals: ContractTotals;
  warranty: string;
  notes: string;
  selections: string[];
  show: CustomShow;
  customer: { name: string; firstName: string; address: string; cityStateZip: string; phone: string; email: string };
  preparedBy: { name: string; phone: string; email: string };
  /** "October 17, 2026". */
  date: string;
  validDays: number;
}

const clean = (xs: readonly string[]) => xs.map((x) => x.trim()).filter(Boolean);

export function customSnapshot(f: CustomForm, ctx: { revision: number; customer: CustomSnapshot["customer"]; preparedBy: CustomSnapshot["preparedBy"]; date: string }): CustomSnapshot {
  const options = f.options.map((o, i) => ({ id: o.id, kind: o.kind, label: optionLabel(o, i, f.options), description: o.description, amountCents: o.amountCents }));
  return {
    market: f.market,
    title: f.title.trim(),
    revision: ctx.revision,
    cover: f.cover,
    intro: f.intro.trim(),
    sections: f.sections.map((s) => ({
      id: s.id,
      heading: s.heading.trim(),
      description: s.description.trim(),
      included: clean(s.included),
      pricing: s.pricing,
      priceCents: sectionTotal(s).priceCents,
      items: s.items.map((i) => ({ description: i.description, quantity: i.quantity, unit: i.unit, unitPriceCents: i.unitPriceCents, amountCents: extend(i.quantity, i.unitPriceCents) })),
    })),
    allowances: f.allowances.map((a) => ({ id: a.id, description: a.description, quantity: a.quantity, unit: a.unit, unitPriceCents: a.unitPriceCents, amountCents: allowanceAmount(a) })),
    options,
    unitPrices: f.unitPrices.map((u) => ({ description: u.description, unit: u.unit, unitPriceCents: u.unitPriceCents })),
    exclusions: clean(f.exclusions),
    qualifications: clean(f.qualifications),
    schedule: { start: f.schedule.start.trim(), duration: f.schedule.duration.trim(), notes: f.schedule.notes.trim() },
    billing: f.billing,
    taxPercentMilli: f.taxPercentMilli,
    discountCents: f.discountCents,
    totals: contractTotals(pricingCore(f)),
    warranty: f.warranty.trim(),
    notes: f.notes.trim(),
    selections: clean(f.selections),
    show: f.show,
    customer: ctx.customer,
    preparedBy: ctx.preparedBy,
    date: ctx.date,
    validDays: f.validDays,
  };
}

/** The pricing of a proposal as sent. */
export function snapshotCore(s: CustomSnapshot): PricingCore {
  return { sectionsCents: s.totals.sectionsCents, allowancesCents: s.totals.allowancesCents, discountCents: s.discountCents, options: s.options, taxPercentMilli: s.taxPercentMilli };
}

/** The schedule of values of a proposal as sent, with the options picked. */
export function snapshotSov(s: CustomSnapshot, picked: readonly string[] = []): SovLine[] {
  return scheduleOfValues(s, picked);
}

/** What signing settles: the options picked, the contract, and its payments. */
export interface CustomSigned {
  picked: string[];
  totals: ContractTotals;
  payments: MilestoneAmount[];
  sov: SovLine[];
}

export function signedTerms(s: CustomSnapshot, picked: readonly string[]): CustomSigned {
  const known = new Set(s.options.map((o) => o.id));
  const ids = [...new Set(picked)].filter((id) => known.has(id));
  const totals = contractTotals(snapshotCore(s), ids);
  return {
    picked: ids,
    totals,
    payments: s.billing.mode === "milestones" ? splitMilestones(s.billing.milestones, totals.contractCents).payments : [],
    sov: s.billing.mode === "progress" ? snapshotSov(s, ids) : [],
  };
}

/** The progress-billing terms in a sentence. */
export function progressTerms(b: Pick<CustomBilling, "retainagePercentMilli" | "stepDown" | "storedMaterials" | "terms">): string {
  const parts = ["Progress billing monthly for the work completed, against the schedule of values."];
  if (b.storedMaterials) parts.push("Bills may include materials stored on site.");
  if (b.retainagePercentMilli > 0) {
    parts.push(
      `Retainage of ${formatPercentMilli(b.retainagePercentMilli)} is held from each payment${b.stepDown ? `, dropping to ${formatPercentMilli(b.stepDown.toPercentMilli)} once the work is ${b.stepDown.atCompletePercent}% complete` : ""}, and is billed on its own at final completion.`,
    );
  }
  if (b.terms.trim()) parts.push(`Payment terms: ${b.terms.trim()}.`);
  return parts.join(" ");
}
